import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { DxPopupComponent } from 'devextreme-angular';
import { OrdersService } from 'src/app/shared/services/order/orders.service';
import { NotificationService } from 'src/app/shared/services/notification.service';
import { ScreenService } from 'src/app/shared/services/screen.service';
import { confirm } from 'devextreme/ui/dialog';

const STATUS_PILL: Record<string, { label: string; cls: string }> = {
  requires_action: { label: 'Open', cls: 'lump-status-pill--open' },
  processing: { label: 'Processing', cls: 'lump-status-pill--processing' },
  succeeded: { label: 'Paid', cls: 'lump-status-pill--paid' },
  failed: { label: 'Failed', cls: 'lump-status-pill--failed' },
  expired: { label: 'Expired', cls: 'lump-status-pill--expired' },
};

@Component({
  selector: 'app-lump-payment-links',
  templateUrl: './lump-payment-links.component.html',
  styleUrls: ['./lump-payment-links.component.scss'],
})
export class LumpPaymentLinksComponent implements OnChanges {
  @Input() orderGroupId: number;
  // Company mode (facility-header entry point): no entry order group — the
  // builder opens directly on the company's unpaid orders.
  @Input() companyId: number = null;
  @Input() visible = false;
  @Output() visibleChange = new EventEmitter<boolean>();
  @Output() changed = new EventEmitter<void>();
  @ViewChild(DxPopupComponent) popup: DxPopupComponent;

  companyName = '';
  orders: any[] = []; // {id, po, facility, buyer_total, created_date, eligible, reason, checked}
  batch: any = null; // {checkout_session_id, total, links: []} | null
  recipient = '';
  loading = false;
  generating = false;
  recipientEmails: string[] = [];
  // Client-side pagination — big companies can have hundreds of unpaid orders.
  page = 0;
  readonly pageSize = 10;
  readonly skeletonRows = Array(5);
  private ordersById: Record<number, any> = {};
  // Repaint re-centres, so it must not run after the user has dragged the
  // popup — only the first settle per open needs it.
  private positionSettled = false;

  constructor(
    private ordersService: OrdersService,
    private notifyService: NotificationService,
    public screen: ScreenService,
  ) {}

  get isSmall() {
    return this.screen.sizes['screen-x-small'] || this.screen.sizes['screen-small'];
  }

  get selected() {
    return this.orders.filter(o => o.checked && o.eligible);
  }

  get total() {
    return this.selected.reduce((sum, o) => sum + o.buyer_total, 0);
  }

  get pageCount() {
    return Math.max(1, Math.ceil(this.orders.length / this.pageSize));
  }

  get pagedOrders() {
    const start = this.page * this.pageSize;
    return this.orders.slice(start, start + this.pageSize);
  }

  setPage(p: number) {
    this.page = Math.min(Math.max(0, p), this.pageCount - 1);
  }

  get hasOpenBatch() {
    return !!this.batch?.links?.some((l: any) => ['requires_action', 'processing'].includes(l.status));
  }

  // Cancellable only while nothing is collected or in flight.
  get canCancel() {
    return this.batch?.links?.some((l: any) => l.status === 'requires_action')
      && !this.batch?.links?.some((l: any) => ['succeeded', 'processing'].includes(l.status));
  }

  toggleItems(o: any) {
    o.expanded = !o.expanded;
  }

  // Open the status panel for a batch this OG is NOT part of (builder row
  // marked 'In another bulk payment') — fetched via that member's own og id.
  viewingOtherBatch = false;

  viewBatch(ogId: number) {
    this.loading = true;
    this.ordersService.getLumpLinks(ogId).subscribe({
      next: res => {
        if (res.data?.links?.length) {
          this.batch = res.data;
          this.viewingOtherBatch = true;
        }
        this.doneLoading();
      },
      error: err => {
        this.notifyService.showErrorMessage(
          this.errorMessage(err, 'Failed to load bulk payment link status'),
        );
        this.doneLoading();
      },
    });
  }

  backToBuilder() {
    this.batch = null;
    this.viewingOtherBatch = false;
  }

  linkPo(link: any) {
    return this.ordersById[link.order_group_id]?.po ?? '—';
  }

  linkFacility(link: any) {
    return this.ordersById[link.order_group_id]?.facility ?? '—';
  }

  statusLabel(s: string) {
    return STATUS_PILL[s]?.label || s;
  }

  statusPillClass(s: string) {
    return STATUS_PILL[s]?.cls || 'lump-status-pill--open';
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.visible?.currentValue && (this.orderGroupId || this.companyId)) {
      this.positionSettled = false;
      this.load();
    }
  }

  load() {
    // Reset before fetching — a reopen (possibly for a different company)
    // must not flash the previous company's orders while loading.
    this.orders = [];
    this.ordersById = {};
    this.companyName = '';
    this.recipientEmails = [];
    this.recipient = '';
    this.batch = null;
    this.page = 0;
    this.viewingOtherBatch = false;
    this.loading = true;
    if (!this.orderGroupId) {
      this.loadCompanyOrders();
      return;
    }
    this.ordersService.getLumpLinks(this.orderGroupId).subscribe({
      next: res => {
        this.viewingOtherBatch = false;
        this.batch = res.data?.links?.length ? res.data : null;
        // Always refresh company orders — the batch's ledger rows don't carry
        // PO/facility, and the builder needs the eligible/ineligible list too.
        this.loadCompanyOrders();
      },
      error: err => {
        this.batch = null;
        this.notifyService.showErrorMessage(
          this.errorMessage(err, 'Failed to load bulk payment link status'),
        );
        this.loadCompanyOrders();
      },
    });
  }

  loadCompanyOrders() {
    const req = this.orderGroupId
      ? this.ordersService.getLumpCompanyOrders(this.orderGroupId)
      : this.ordersService.getLumpCompanyOrdersByCompany(this.companyId);
    req.subscribe({
      next: res => {
        this.companyName = res.data?.company_name || '';
        this.recipientEmails = res.data?.recipient_emails || [];
        this.orders = (res.data?.orders || []).map((o: any) => ({
          ...o,
          checked: o.id === this.orderGroupId && o.eligible,
        }));
        this.ordersById = {};
        this.orders.forEach(o => (this.ordersById[o.id] = o));
        // Open on the page holding the entry order (checked state spans pages).
        const entryIdx = this.orders.findIndex(o => o.id === this.orderGroupId);
        this.page = entryIdx > -1 ? Math.floor(entryIdx / this.pageSize) : 0;
        this.doneLoading();
      },
      error: err => {
        this.notifyService.showErrorMessage(
          this.errorMessage(err, 'Failed to load orders'),
        );
        this.doneLoading();
      },
    });
  }

  generate() {
    const n = this.selected.length;
    if (this.generating || !n || !this.recipient) {
      return;
    }
    confirm(
      `<i>Once this bulk payment link is created, on-site payment for these ${n} order(s) ` +
      'is disabled until it is paid or cancelled. Continue?</i>',
      'Create bulk payment link?',
    ).then(ok => {
      if (ok) {
        this._generate();
      }
    });
  }

  private _generate() {
    this.loading = true;
    this.generating = true;
    this.ordersService.sendLumpLinks({
      order_group_ids: this.selected.map(o => o.id),
      recipient: this.recipient,
    }).subscribe({
      next: () => {
        this.notifyService.showSuccessMessage('Bulk payment link sent');
        this.recipient = '';
        this.generating = false;
        this.load();
        this.changed.emit();
      },
      error: err => {
        this.notifyService.showErrorMessage(
          this.errorMessage(err, 'Failed to create bulk payment link'),
        );
        this.doneLoading();
        this.generating = false;
      },
    });
  }

  cancelBatch() {
    confirm(
      '<i>This cancels the bulk payment link — the recipient\'s link will stop working and ' +
      'standard payment will be re-enabled for these orders. Continue?</i>',
      'Cancel bulk payment link?',
    ).then(ok => {
      if (!ok) {
        return;
      }
      this.ordersService.cancelLumpLinks(this.batch.checkout_session_id).subscribe({
        next: () => {
          this.notifyService.showSuccessMessage('Bulk payment link cancelled');
          this.load();
          this.changed.emit();
        },
        error: err => this.notifyService.showErrorMessage(
          this.errorMessage(err, 'Failed to cancel bulk payment link'),
        ),
      });
    });
  }

  copyLink() {
    const url = this.batch?.links?.[0]?.pay_url;
    if (!url) {
      return;
    }
    navigator.clipboard.writeText(url)
      .then(() => this.notifyService.showSuccessMessage('Link copied to clipboard'));
  }

  private errorMessage(err: any, fallback: string): string {
    return err?.error?.error
      || (typeof err?.error === 'string' ? err.error : null)
      || (typeof err === 'string' ? err : null)
      || err?.message
      || fallback;
  }

  // height:auto measures once, while the skeleton is the only content, so the
  // grown list overflows the viewport. Re-measure after the real content renders.
  private doneLoading() {
    this.loading = false;
    if (this.positionSettled) {
      return;
    }
    this.positionSettled = true;
    setTimeout(() => this.popup?.instance?.repaint());
  }

  onPopupVisibleChange(value: boolean) {
    this.visible = value;
    this.visibleChange.emit(value);
  }
}
